import { useCallback, useEffect, useRef, useState } from 'react';
import { tasksService } from '../../services/tasksService';
import { useLanguageStore } from '../../stores/languageStore';
import { useNoAiStore } from '../../stores/noAiStore';
import { useTenantStore } from '../../stores/tenantStore';

interface GreetingStream {
    greeting: string;
    /** True only during the first stream; Home gates its content skeleton on it. */
    isGreetingStreaming: boolean;
}

/**
 * Streams Home's AI greeting. `bridgeAccessReady` gates it because the greeting is generated from relance
 * data and gated server-side on the same access decision (TaskService.GetGreetingAsync). The first
 * greeting predates ranking, so it is re-streamed once when `fillReached` first turns true.
 */
export function useGreetingStream(bridgeAccessReady: boolean, fillReached: boolean): GreetingStream {
    const lang = useLanguageStore(s => s.lang);
    const noAi = useNoAiStore(s => s.noAi);
    const tenantCode = useTenantStore(s => s.tenantCode);
    const [greeting, setGreeting] = useState('');
    const [isGreetingStreaming, setIsGreetingStreaming] = useState(true);
    const abortRef = useRef<AbortController | null>(null);

    // A re-stream (`isInitial` false) keeps the previous greeting on screen until its first chunk
    // replaces it, and never brings the content skeleton back.
    const streamGreeting = useCallback(async (isInitial: boolean) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        if (isInitial) {
            setGreeting('');
            setIsGreetingStreaming(true);
        }

        let acc = '';
        try {
            for await (const chunk of tasksService.streamGreeting(lang, noAi, controller.signal)) {
                acc += chunk;
                setGreeting(acc);
            }
        } catch (e) {
            if ((e as Error).name !== 'AbortError') console.error('Greeting stream error:', e);
        } finally {
            if (isInitial) setIsGreetingStreaming(false);
        }
    }, [lang, noAi]);

    useEffect(() => {
        if (!bridgeAccessReady) return;
        void streamGreeting(true);
        return () => abortRef.current?.abort();
    }, [tenantCode, bridgeAccessReady, streamGreeting]);

    const wasFillReachedRef = useRef(false);
    useEffect(() => {
        if (fillReached && !wasFillReachedRef.current) void streamGreeting(false);
        wasFillReachedRef.current = fillReached;
    }, [fillReached, streamGreeting]);

    return { greeting, isGreetingStreaming };
}
